import { LengthOfStayBucket } from '@sales-app/context/workspace/lengthOfStayBucket.util'
import { DisplayCity } from '@sales-app/types/DisplayCity'
import { parseISO } from 'date-fns'
import { createContext, useContext, useEffect, useState } from 'react'

import { useLocalStorage } from '@/hooks/useLocalStorage'

export type SortBy = {
  desc: boolean
  id: string
}

export interface FiltersState {
  agentId: null | number
  bookablePersonType: null | string
  createdAtEndDate?: Date
  createdAtStartDate?: Date
  displayCity: DisplayCity | null
  // Absent from filters saved before this field existed
  lengthOfStayBucket?: LengthOfStayBucket | null
  moveInWithin: null | number
  // Number of days to move in
  potentialBookingIds: null | number[]
  potentialBookingStatuses: null | StatusFilter
  searchString: string | null
  sortBy: null | SortBy
  unassigned: boolean | null
}

export interface StatusFilter {
  descriptions: null | string[]
  inclusive: null | InclusiveExclusive
}

export enum InclusiveExclusive {
  EXCLUSIVE = 'EXCLUSIVE',
  INCLUSIVE = 'INCLUSIVE',
}

export enum PotentialBookingStatuses {
  APPROVED = 'Approved',
  BOOKED = 'Booked',
  NEW = 'New',
}

export const currentFiltersKey = 'active-potential-bookings-filters'

export const defaultFilterState: FiltersState = {
  agentId: null,
  bookablePersonType: null,
  lengthOfStayBucket: null,
  moveInWithin: null,
  potentialBookingIds: null,
  potentialBookingStatuses: null,
  searchString: null,
  unassigned: null,
  displayCity: null,
  sortBy: null,
}

interface FiltersContextType extends FiltersState {
  setFilters: (filters: Partial<FiltersState>) => void
  setSearchString: (val: string) => void
  setSortBy: (val: SortBy | null) => void
  storedFiltersLoaded: boolean
}

export const FiltersContextStore = createContext<FiltersContextType | undefined>(undefined)

const PotentialBookingFiltersContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [storedFilters, setStoredFilters] = useLocalStorage(currentFiltersKey, defaultFilterState)

  const [storedFiltersLoaded, setStoredFiltersLoaded] = useState(false)

  const [filtersState, setFiltersState] = useState(defaultFilterState)

  const setFilters = (filters: Partial<FiltersState>) => {
    setFiltersState((prevState) => {
      return {
        ...prevState,
        ...filters,
      }
    })
  }

  const setSearchString = (val: string) => {
    setFiltersState((prevState) => {
      return {
        ...prevState,
        searchString: val.length > 0 ? val : null,
      }
    })
  }

  const setSortBy = (val: SortBy | null) => {
    setFiltersState((prevState) => {
      return {
        ...prevState,
        sortBy: val ? val : null,
      }
    })
  }

  useEffect(() => {
    if (!storedFiltersLoaded) {
      if (storedFilters.createdAtStartDate) {
        storedFilters.createdAtStartDate = parseISO(storedFilters.createdAtStartDate.toString())
      }

      if (storedFilters.createdAtEndDate) {
        storedFilters.createdAtEndDate = parseISO(storedFilters.createdAtEndDate.toString())
      }
      setFiltersState(storedFilters)
      setStoredFiltersLoaded(true)
    }
  }, [storedFiltersLoaded, storedFilters])

  useEffect(() => {
    if (storedFiltersLoaded) {
      setStoredFilters(filtersState)
    }
  }, [storedFiltersLoaded, filtersState, setStoredFilters])

  return (
    <FiltersContextStore.Provider
      value={{
        ...filtersState,
        setSearchString,
        setFilters,
        setSortBy,
        storedFiltersLoaded,
      }}
    >
      {children}
    </FiltersContextStore.Provider>
  )
}

export const usePotentialBookingFiltersContext = () => {
  const context = useContext(FiltersContextStore)

  if (context === undefined) {
    throw new Error('useFiltersContext must be used within a FiltersContextProvider')
  }

  return context
}

export default PotentialBookingFiltersContextProvider
